import type { FC, RefObject } from 'react'
import { PropsWithChildren } from 'react'
import { Box, Typography, useTheme } from '@mui/material'

import { ParcelCat } from '@dhl/illustrations'

import { pulse } from './pulse.js'
import { illustrationHeight, svgHeight, triggerHeight } from './constants.js'

type Props = PropsWithChildren<{
  inputRef: RefObject<HTMLDivElement | null>
  translateY: number
  isRetracting: boolean
  isRefreshing: boolean
  lastUpdateText: string
}>

export const PullContainer: FC<Props> = ({
  children,
  inputRef,
  translateY,
  isRetracting,
  isRefreshing,
  lastUpdateText,
}) => {
  const { breakpoints } = useTheme()
  const isHidden = translateY < 0

  return (
    <Box
      ref={inputRef}
      data-test="pull-container"
      data-testid="pull-container"
      position="relative"
      style={{
        transform: translateY >= 0 ? `translateY(${translateY}px)` : undefined,
      }}
      sx={{
        transition: 'transform 0.05s ease-out',
        transitionDuration: isRetracting ? '.2s' : '.05s',
        top: isHidden ? 0 : -triggerHeight,
        [breakpoints.up('lg')]: {
          top: 0,
        },
      }}
      zIndex={1}
    >
      <Box
        alignItems="center"
        data-test="refresh"
        flexDirection="column"
        height={illustrationHeight}
        justifyContent="center"
        sx={{
          display: {
            xs: isHidden ? 'none' : 'flex',
            xl: 'none',
          },
        }}
      >
        <ParcelCat sx={{ width: 200, height: svgHeight, opacity: 1 }} />
        <Typography
          color="grey.600"
          variant="caption"
          sx={{
            animation: isRefreshing ? `${pulse} .8s linear 0s infinite alternate` : undefined,
          }}
        >
          {lastUpdateText}
        </Typography>
      </Box>
      <Box pt={isHidden ? 0 : 2}>{children}</Box>
    </Box>
  )
}
